/**
 * GiftPage.js — Public "Gift someone Talebound" storefront (TB-087).
 *
 * No login required. Three views, selected by route (index.js):
 *   /gift               → buy form (guest checkout → Stripe)
 *   /gift/success?code= → confirmation with the gift code + printable card link
 *   /gift/print?code=   → printable gift card (print stylesheet)
 *
 * The giver buys ($89.99, guest checkout). After Stripe payment clears, the
 * Order Lambda webhook issues a single-use 100% coupon tied to the gift code.
 * The recipient redeems that code when they order their own hardcover.
 *
 * Props:
 *   view: 'buy' | 'success' | 'print'
 *   code: string | null  — gift code from the URL (success/print views)
 */

import React from 'react';
import { trackEvent } from './utils/analytics';
import Reviews from './Reviews';
import VideoEmbed from './components/VideoEmbed';
import { useSaleOffer, SALE_OFFER } from './hooks/useSaleOffer';

const ORDER_API_BASE = 'https://5nrm0mndm9.execute-api.us-east-1.amazonaws.com/prod';
export const GIFT_PRICE = '$89.99';
// F-163-GIFT: the gift flow reuses the SAME cap-aware single source as the book flow (useSaleOffer →
// /order/validate-promo). No hardcoded price seed — the discount is confirmed by the server and reverts
// honestly if TALE30 is capped/inactive. Copy rules (F-163-GO): no "launch", no urgency, no counts.

// Founder contact for the gift page note. Phone renders only when set.
const FOUNDER_PHONE = '(708) 918-4633';
const FOUNDER_EMAIL = 'alex@taleboundbook.com';

// Pre-filled gift note (editable). Must stay under the 200-char gift card limit.
const DEFAULT_GIFT_NOTE = "For all the stories you've told me, and the ones I haven't heard yet. I can't wait to hold them in my hands.";

const STYLES = `
@import url('https://fonts.googleapis.com/css2?family=Cinzel:wght@400;500;600&family=Lora:ital,wght@0,400;0,500;0,600;0,700;1,400&display=swap');

* { margin: 0; padding: 0; box-sizing: border-box; }

/* ─── Page shell ─── */
.gp-page {
  min-height: 100svh;
  background: #fbf8f1;
  display: flex;
  flex-direction: column;
  align-items: center;
}

/* ── Header ── */
.gp-header {
  position: sticky;
  top: 0;
  z-index: 50;
  width: 100%;
  padding: 18px 32px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  background: rgba(251,248,241,0.96);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  border-bottom: 1px solid rgba(15,23,42,0.08);
  box-shadow: 0 2px 16px rgba(15,23,42,0.04);
}
.gp-logo {
  display: flex;
  align-items: baseline;
  gap: 6px;
  text-decoration: none;
}
.gp-logo-name {
  font-family: 'Lora', serif;
  font-size: 1.4rem;
  font-weight: 700;
  letter-spacing: 1px;
  color: #0f172a;
}
.gp-logo-tag {
  font-family: 'Lora', serif;
  font-size: 0.65rem;
  font-style: italic;
  letter-spacing: 1px;
  color: rgba(15,23,42,0.5);
}
@media (max-width: 600px) { .gp-logo-tag { display: none; } }
.gp-nav-link {
  font-family: 'Lora', serif;
  font-size: 0.85rem;
  color: #7c2d12;
  text-decoration: none;
  font-weight: 600;
  white-space: nowrap;
}
.gp-nav-link:hover { text-decoration: underline; }

/* ─── Section band system ───
   Every top-level section is a full-width band.
   .gp-band         — the full-width container
   .gp-band-inner   — max-width centred column inside the band
   Ground tones rotate: cream / white / sand so sections read as distinct blocks.
─── */
.gp-band {
  width: 100%;
}
.gp-band-cream  { background: #f6f1e8; }
.gp-band-white  { background: #ffffff; }
.gp-band-sand   { background: #f3ede3; }
.gp-band-parchment { background: #fbf8f1; }
.gp-band-hero   { background: #f8f5ef; }

.gp-band-inner {
  max-width: 880px;
  margin: 0 auto;
  padding: 80px 40px;
}
.gp-band-inner-wide {
  max-width: 1040px;
  margin: 0 auto;
  padding: 80px 40px;
}
@media (max-width: 640px) {
  .gp-band-inner,
  .gp-band-inner-wide { padding: 56px 20px; }
}

/* ─── Type scale ───
   Cinzel for display headings. Lora for everything else.
   Scale: eyebrow 0.68rem / h2 2rem desktop / h3 1.2rem / body 1.05rem / small 0.92rem
─── */
.gp-eyebrow {
  font-family: 'Lora', serif;
  font-size: 0.68rem;
  letter-spacing: 3.5px;
  color: #6f5512;
  text-transform: uppercase;
  font-weight: 600;
  margin-bottom: 14px;
}
.gp-section-heading {
  font-family: 'Lora', serif;
  font-size: 2rem;
  font-weight: 500;
  color: #0f172a;
  line-height: 1.25;
  letter-spacing: 0.02em;
  margin-bottom: 20px;
}
.gp-section-sub {
  font-family: 'Lora', serif;
  font-size: 1.05rem;
  color: #4e3f2c;
  line-height: 1.8;
  max-width: 600px;
}
@media (max-width: 640px) {
  .gp-section-heading { font-size: 1.55rem; }
}

/* ─── Hero band: photo-split layout ─── */
.gp-hero-band {
  width: 100%;
}
/* Desktop: side-by-side. Text left, photo right */
.gp-hero-split {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 64px;
  align-items: center;
}
.gp-hero-text {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}
.gp-hero-text .gp-eyebrow { margin-bottom: 16px; }
.gp-hero-title {
  font-family: 'Lora', serif;
  font-size: 2.85rem;
  font-weight: 500;
  color: #0f172a;
  line-height: 1.15;
  letter-spacing: 0.03em;
  margin-bottom: 20px;
}
.gp-hero-sub {
  font-family: 'Lora', serif;
  font-size: 1.1rem;
  color: #4e3f2c;
  line-height: 1.8;
  margin-bottom: 28px;
}
.gp-hero-price {
  font-family: 'Lora', serif;
  font-size: 1.1rem;
  color: #0f172a;
  margin-bottom: 24px;
  display: flex;
  align-items: baseline;
  gap: 12px;
  flex-wrap: wrap;
}
.gp-hero-price-was {
  color: #6f5f4d;
  text-decoration: line-through;
  font-size: 1rem;
}
.gp-hero-price-now {
  font-size: 1.35rem;
  color: #0f172a;
}
.gp-hero-price-note {
  font-family: 'Lora', serif;
  font-size: 0.85rem;
  color: #6f5512;
  font-weight: 600;
  letter-spacing: 0.5px;
}
.gp-hero-switch {
  font-family: 'Lora', serif;
  font-size: 0.88rem;
  color: #62553f;
  line-height: 1.55;
  margin-top: 18px;
}
.gp-hero-switch b { color: #3c4653; }
.gp-hero-switch a { color: #7c2d12; font-weight: 700; text-decoration: none; }
.gp-hero-switch a:hover { text-decoration: underline; }

/* Hero photo column */
.gp-hero-photo {
  width: 100%;
}
.gp-hero-photo img {
  width: 100%;
  display: block;
  border-radius: 20px;
  box-shadow: 0 24px 64px rgba(100,60,20,0.18), 0 4px 16px rgba(100,60,20,0.10);
}
.gp-photo-caption {
  font-family: 'Lora', serif;
  font-style: italic;
  font-size: 0.88rem;
  color: #6a5236;
  text-align: center;
  margin-top: 12px;
  line-height: 1.5;
}

/* Mobile: stack with photo below text */
@media (max-width: 860px) {
  .gp-hero-split {
    grid-template-columns: 1fr;
    gap: 36px;
  }
  .gp-hero-text {
    align-items: center;
    text-align: center;
  }
  .gp-hero-title { font-size: 2.1rem; }
  .gp-hero-price { justify-content: center; }
}
@media (max-width: 480px) {
  .gp-hero-title { font-size: 1.85rem; }
}

/* ─── Primary CTA button ─── */
.gp-cta-btn {
  display: inline-block;
  font-family: 'Lora', serif;
  font-size: 1.15rem;
  font-weight: 700;
  letter-spacing: 0.3px;
  color: #2a1c06;
  background: linear-gradient(180deg, #d9b860, #c9a84c);
  border: none;
  border-radius: 12px;
  padding: 20px 48px;
  cursor: pointer;
  transition: all 0.25s;
  box-shadow: 0 6px 20px rgba(201,168,76,0.40);
  text-decoration: none;
}
.gp-cta-btn:hover {
  background: linear-gradient(180deg, #e2c26d, #d3b257);
  transform: translateY(-1px);
  box-shadow: 0 10px 28px rgba(201,168,76,0.52);
}
.gp-cta-btn-full {
  display: block;
  width: 100%;
  text-align: center;
  padding: 20px 32px;
}
@media (max-width: 640px) {
  .gp-cta-btn { padding: 18px 36px; font-size: 1.05rem; }
}

/* ─── How it works: 3 steps ─── */
.gp-steps {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
  margin-top: 48px;
}
.gp-step {
  text-align: left;
  padding: 32px 28px;
  background: #fdf6e8;
  border-radius: 16px;
  border: 1px solid rgba(201,168,76,0.22);
}
.gp-step-num {
  font-family: 'Lora', serif;
  font-size: 2.25rem;
  font-weight: 400;
  color: #6f5512;
  line-height: 1;
  margin-bottom: 16px;
  opacity: 0.8;
}
.gp-step-title {
  font-family: 'Lora', serif;
  font-size: 1.05rem;
  font-weight: 700;
  color: #0f172a;
  margin-bottom: 10px;
}
.gp-step-text {
  font-family: 'Lora', serif;
  font-size: 0.95rem;
  color: #4a5568;
  line-height: 1.65;
}
@media (max-width: 720px) {
  .gp-steps { grid-template-columns: 1fr; gap: 14px; }
  .gp-step { padding: 24px 22px; }
}

/* ─── Video focal band ─── */
.gp-video-band { text-align: center; }
.gp-video-focal {
  max-width: 860px;
  margin: 40px auto 0;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 24px 64px rgba(100,60,20,0.14), 0 4px 16px rgba(100,60,20,0.08);
}

/* ─── Offer spotlight (price moment above form) ─── */
.gp-offer-spotlight {
  text-align: center;
  padding: 48px 40px;
  background: linear-gradient(160deg, #3a1a08 0%, #1e0e06 100%);
  border-radius: 20px;
  margin-bottom: 40px;
}
.gp-offer-spotlight .gp-eyebrow {
  color: rgba(201,168,76,0.85);
  margin-bottom: 12px;
}
.gp-offer-headline {
  font-family: 'Lora', serif;
  font-size: 1.5rem;
  font-weight: 500;
  color: #f5ede0;
  margin-bottom: 24px;
  line-height: 1.3;
}
.gp-offer-price-row {
  display: flex;
  align-items: baseline;
  justify-content: center;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 10px;
}
.gp-offer-price-was {
  font-family: 'Lora', serif;
  font-size: 1.25rem;
  color: rgba(245,237,224,0.60);
  text-decoration: line-through;
}
.gp-offer-price-now {
  font-family: 'Lora', serif;
  font-size: 3rem;
  font-weight: 500;
  color: #c9a84c;
  line-height: 1;
}
.gp-offer-code-note {
  font-family: 'Lora', serif;
  font-size: 0.9rem;
  color: rgba(245,237,224,0.65);
  margin-top: 8px;
  letter-spacing: 0.5px;
}
@media (max-width: 640px) {
  .gp-offer-spotlight { padding: 36px 24px; }
  .gp-offer-price-now { font-size: 2.25rem; }
}

/* ─── Magic / demo section ─── */
.gp-magic-section { text-align: center; }
.gp-video-container {
  margin: 40px auto 0;
  max-width: 720px;
}
.gp-video-label {
  font-family: 'Lora', serif;
  font-style: italic;
  font-size: 1rem;
  color: #62553f;
  margin-bottom: 16px;
}

/* Talk → book demo */
.gp-demo {
  max-width: 640px;
  margin: 48px auto 0;
}
.gp-demo-chat {
  width: 100%;
  background: #fff;
  border: 1px solid rgba(15,23,42,0.08);
  border-radius: 16px;
  padding: 24px 20px;
  box-shadow: 0 8px 32px rgba(15,23,42,0.06);
  display: flex;
  flex-direction: column;
  gap: 12px;
  text-align: left;
}
.gp-chat-q, .gp-chat-a { max-width: 88%; padding: 12px 16px; }
.gp-chat-q {
  align-self: flex-start;
  background: #f3ece0;
  border-radius: 14px 14px 14px 4px;
}
.gp-chat-a {
  align-self: flex-end;
  background: #fbf4e7;
  border: 1px solid rgba(201,168,76,0.25);
  border-radius: 14px 14px 4px 14px;
}
.gp-chat-who {
  font-family: 'Lora', serif;
  font-weight: 700;
  font-size: 0.62rem;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  margin-bottom: 4px;
}
.gp-chat-q .gp-chat-who { color: #6f5512; }
.gp-chat-a .gp-chat-who { color: #7c2d12; }
.gp-chat-text {
  font-family: 'Lora', serif;
  font-size: 0.95rem;
  line-height: 1.5;
  color: #3a2f22;
}
.gp-chat-a .gp-chat-text { font-style: italic; }

.gp-demo-arrow {
  text-align: center;
  margin: 20px 0;
  font-family: 'Lora', serif;
  font-size: 0.72rem;
  letter-spacing: 0.12em;
  color: #6f5512;
  text-transform: uppercase;
}
.gp-demo-book {
  width: 100%;
  background: #fff;
  border: 1px solid rgba(15,23,42,0.10);
  border-radius: 16px;
  padding: 24px 24px;
  box-shadow: 0 12px 36px rgba(15,23,42,0.08);
  text-align: left;
}
.gp-demo-book-label {
  font-family: 'Lora', serif;
  font-weight: 700;
  font-size: 0.68rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #7c2d12;
  margin-bottom: 12px;
}
.gp-demo-book-text {
  font-family: 'Lora', serif;
  font-size: 1.05rem;
  line-height: 1.7;
  color: #2a3342;
}
.gp-demo-foot {
  font-family: 'Lora', serif;
  font-style: italic;
  font-size: 0.97rem;
  color: #535a68;
  text-align: center;
  max-width: 480px;
  margin: 24px auto 0;
  line-height: 1.6;
}

/* ─── Feature list (replaces how-list) ─── */
.gp-feature-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: 40px;
  text-align: left;
}
.gp-feature-item {
  display: flex;
  gap: 14px;
  align-items: flex-start;
  font-family: 'Lora', serif;
  font-size: 1rem;
  color: #4e3f2c;
  line-height: 1.65;
}
.gp-feature-dot {
  flex-shrink: 0;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #c9a84c;
  margin-top: 8px;
}
.gp-feature-item b { color: #0f172a; font-weight: 700; }

/* QR callout */
.gp-qr-callout {
  display: flex;
  gap: 16px;
  align-items: center;
  text-align: left;
  margin-top: 20px;
  padding: 20px 22px;
  background: rgba(201,168,76,0.06);
  border: 1px solid rgba(201,168,76,0.3);
  border-radius: 16px;
}
.gp-qr-art {
  position: relative;
  width: 56px;
  height: 56px;
  flex-shrink: 0;
  border-radius: 10px;
  background: #fff;
  border: 1px solid rgba(15,23,42,0.10);
  display: flex;
  align-items: center;
  justify-content: center;
}
.gp-qr-text {
  font-family: 'Lora', serif;
  font-size: 0.93rem;
  color: #4a5568;
  line-height: 1.55;
}
.gp-qr-text b { display: block; color: #0f172a; margin-bottom: 3px; }

/* ─── Reviews ─── */
.gp-reviews-band { text-align: center; }

/* ─── Prompt packs ─── */
.gp-packs { text-align: center; }
.gp-pack-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  margin-top: 36px;
}
.gp-pack-card {
  background: #fbf8f1;
  border: 1px solid rgba(15,23,42,0.08);
  border-radius: 14px;
  padding: 20px 18px;
  text-align: left;
}
.gp-pack-name {
  font-family: 'Lora', serif;
  font-weight: 600;
  font-size: 0.9rem;
  color: #16233f;
  margin-bottom: 8px;
}
.gp-pack-q {
  font-family: 'Lora', serif;
  font-style: italic;
  font-size: 0.97rem;
  color: #4a5568;
  line-height: 1.45;
}
.gp-pack-foot {
  font-family: 'Lora', serif;
  font-size: 0.93rem;
  color: #535a68;
  margin-top: 24px;
  line-height: 1.6;
}
@media (max-width: 720px) {
  .gp-pack-grid { grid-template-columns: repeat(2, 1fr); gap: 12px; }
}
@media (max-width: 460px) {
  .gp-pack-grid { grid-template-columns: 1fr; }
}

/* ─── Checkout / offer section ─── */
.gp-offer-band { text-align: center; }
.gp-offer-band .gp-band-inner { max-width: 720px; }

/* ─── Form card ─── */
.gp-card {
  width: 100%;
  background: #fff;
  border: 1px solid rgba(15,23,42,0.08);
  border-radius: 20px;
  padding: 40px 36px;
  box-shadow: 0 16px 52px rgba(15,23,42,0.06);
}
.gp-card-checkout {
  border: 1.5px solid rgba(201,168,76,0.45);
  box-shadow: 0 20px 60px rgba(124,45,18,0.08);
  scroll-margin-top: 24px;
}
.gp-form-head { text-align: center; margin-bottom: 28px; }
.gp-form-title {
  font-family: 'Lora', serif;
  font-size: 1.65rem;
  color: #0f172a;
  margin-bottom: 8px;
}
.gp-form-sub {
  font-family: 'Lora', serif;
  font-size: 0.95rem;
  color: #4a5568;
  line-height: 1.55;
}

.gp-form { display: flex; flex-direction: column; gap: 22px; }
.gp-label {
  font-family: 'Lora', serif;
  font-size: 0.7rem;
  letter-spacing: 2px;
  color: #3c4653;
  text-transform: uppercase;
  margin-bottom: 6px;
  display: block;
}
.gp-input {
  font-family: 'Lora', serif;
  font-size: 1rem;
  color: #0f172a;
  background: #fbf8f1;
  border: 1px solid rgba(15,23,42,0.12);
  border-radius: 8px;
  padding: 13px 16px;
  width: 100%;
  outline: none;
  transition: border-color 0.2s;
}
.gp-input:focus { border-color: #7c2d12; }
.gp-input-error { border-color: #c0392b; box-shadow: 0 0 0 2px rgba(192,57,43,0.15); }
.gp-field-error { font-family: 'Lora', serif; font-size: 0.85rem; color: #c0392b; margin-top: 5px; }
.gp-textarea {
  font-family: 'Lora', serif;
  font-size: 1rem;
  color: #0f172a;
  background: #fbf8f1;
  border: 1px solid rgba(15,23,42,0.12);
  border-radius: 8px;
  padding: 14px 16px;
  width: 100%;
  min-height: 90px;
  outline: none;
  line-height: 1.6;
  resize: vertical;
  transition: border-color 0.2s;
}
.gp-textarea:focus { border-color: #7c2d12; }
.gp-textarea::placeholder, .gp-input::placeholder { color: #c4b8a8; font-style: italic; }
.gp-charcount {
  font-family: 'Lora', serif;
  font-size: 0.72rem;
  color: #6f5f4d;
  text-align: right;
  margin-top: 4px;
}

/* ─── Price row ─── */
.gp-price-row {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  padding: 18px 0;
  border-top: 1px solid rgba(15,23,42,0.08);
}
.gp-price-label {
  font-family: 'Lora', serif;
  font-size: 0.95rem;
  color: #3c4653;
}
.gp-price-amount {
  font-family: 'Lora', serif;
  font-size: 1.55rem;
  color: #0f172a;
}
.gp-price-was {
  font-family: 'Lora', serif;
  font-size: 1rem;
  color: #6f5f4d;
  text-decoration: line-through;
  margin-right: 10px;
}

/* ─── Promo ─── */
.gp-promo-row { display: flex; gap: 8px; }
.gp-promo-row .gp-input { flex: 1; }
.gp-promo-btn {
  font-family: 'Lora', serif;
  font-size: 0.95rem;
  font-weight: 600;
  color: #fff;
  background: #7c2d12;
  border: none;
  border-radius: 8px;
  padding: 0 22px;
  cursor: pointer;
  white-space: nowrap;
  transition: background-color 0.2s;
}
.gp-promo-btn:hover:not(:disabled) { background: #8f3517; }
.gp-promo-btn:disabled { opacity: 0.5; cursor: not-allowed; }
.gp-promo-ok { font-family: 'Lora', serif; font-size: 0.85rem; color: #4a7c5c; margin-top: 6px; }
.gp-promo-err { font-family: 'Lora', serif; font-size: 0.85rem; color: #a04040; margin-top: 6px; }

/* ─── What's included ─── */
.gp-includes {
  font-family: 'Lora', serif;
  font-size: 0.9rem;
  color: #3c4653;
  line-height: 1.65;
  background: rgba(201,168,76,0.04);
  border: 1px solid rgba(201,168,76,0.18);
  border-radius: 10px;
  padding: 16px 18px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.gp-includes-item { display: flex; gap: 12px; align-items: baseline; }
.gp-includes-check { color: #c9a84c; font-weight: 700; flex-shrink: 0; }
.gp-hint {
  font-family: 'Lora', serif;
  font-size: 0.78rem;
  color: #535a68;
  margin-top: 4px;
}
.gp-secure {
  font-family: 'Lora', serif;
  font-size: 0.8rem;
  color: #535a68;
  text-align: center;
  margin-top: 2px;
}

/* ─── Guarantee ─── */
.gp-guarantee {
  display: flex;
  align-items: center;
  gap: 12px;
  background: rgba(201,168,76,0.05);
  border: 1px solid rgba(201,168,76,0.28);
  border-radius: 10px;
  padding: 14px 16px;
}
.gp-guarantee-icon { flex: 0 0 auto; display: flex; line-height: 0; }
.gp-guarantee-text {
  font-family: 'Lora', serif;
  font-size: 0.87rem;
  line-height: 1.5;
  color: #3c4653;
}
.gp-guarantee-text b { color: #0f172a; }

/* ─── Submit button ─── */
.gp-submit {
  font-family: 'Lora', serif;
  font-size: 1.2rem;
  font-weight: 700;
  letter-spacing: 0.5px;
  color: #2a1c06;
  background: linear-gradient(180deg, #d9b860, #c9a84c);
  border: none;
  border-radius: 12px;
  padding: 20px 32px;
  cursor: pointer;
  transition: all 0.25s;
  width: 100%;
  box-shadow: 0 6px 18px rgba(201,168,76,0.4);
}
.gp-submit:hover:not(:disabled) {
  background: linear-gradient(180deg, #e2c26d, #d3b257);
  transform: translateY(-1px);
  box-shadow: 0 10px 28px rgba(201,168,76,0.52);
}
.gp-submit:disabled { opacity: 0.5; cursor: not-allowed; }

.gp-error {
  font-family: 'Lora', serif;
  font-size: 0.85rem;
  color: #a04040;
  text-align: center;
  padding: 12px;
  background: rgba(160,64,64,0.05);
  border-radius: 8px;
}

/* ─── FAQ ─── */
.gp-faq { width: 100%; }
.gp-faq-item { border-top: 1px solid rgba(15,23,42,0.08); }
.gp-faq-item:last-child { border-bottom: 1px solid rgba(15,23,42,0.08); }
.gp-faq-q {
  font-family: 'Lora', serif;
  font-size: 1rem;
  font-weight: 700;
  color: #0f172a;
  padding: 18px 4px;
  cursor: pointer;
  list-style: none;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 14px;
}
.gp-faq-q:hover { color: #7c2d12; }
.gp-faq-q::-webkit-details-marker { display: none; }
.gp-faq-q::after {
  content: '+';
  font-size: 1.5rem;
  font-weight: 400;
  color: #6f5512;
  flex-shrink: 0;
  line-height: 1;
}
.gp-faq-item[open] > .gp-faq-q::after { content: '\\2212'; }
.gp-faq-a {
  font-family: 'Lora', serif;
  font-size: 0.95rem;
  line-height: 1.65;
  color: #4a5568;
  padding: 0 4px 20px;
}

/* ─── Founder note ─── */
.gp-founder {
  text-align: center;
  max-width: 600px;
  margin: 0 auto;
}
.gp-founder-eyebrow {
  font-family: 'Lora', serif;
  font-size: 0.68rem;
  letter-spacing: 3.5px;
  color: #6f5512;
  text-transform: uppercase;
  font-weight: 600;
  margin-bottom: 14px;
}
.gp-founder-text {
  font-family: 'Lora', serif;
  font-size: 1rem;
  color: #3c4653;
  line-height: 1.75;
  margin: 0 0 20px;
}
.gp-founder-contact {
  display: flex;
  justify-content: center;
  gap: 24px;
  flex-wrap: wrap;
  margin-bottom: 16px;
}
.gp-founder-contact a {
  font-family: 'Lora', serif;
  font-size: 0.97rem;
  font-weight: 600;
  color: #7c2d12;
  text-decoration: none;
}
.gp-founder-contact a:hover { text-decoration: underline; }
.gp-founder-sig {
  font-family: 'Lora', serif;
  font-style: italic;
  font-size: 0.9rem;
  color: #535a68;
}

/* ─── Sticky mobile checkout bar ─── */
.gp-sticky {
  position: fixed;
  left: 0; right: 0; bottom: 0;
  z-index: 60;
  padding: 12px 16px calc(12px + env(safe-area-inset-bottom));
  background: rgba(251,248,241,0.96);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border-top: 1px solid rgba(15,23,42,0.10);
  box-shadow: 0 -4px 22px rgba(15,23,42,0.10);
  transition: transform 0.28s ease;
}
.gp-sticky-hidden { transform: translateY(120%); }
.gp-sticky-inner {
  max-width: 680px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  gap: 18px;
}
.gp-sticky-label {
  display: none;
  flex: 1;
  font-family: 'Lora', serif;
  font-size: 0.95rem;
  color: #3c4653;
}
.gp-sticky-btn {
  width: 100%;
  font-family: 'Lora', serif;
  font-size: 1.12rem;
  font-weight: 700;
  color: #2a1c06;
  background: linear-gradient(180deg, #d9b860, #c9a84c);
  border: none;
  border-radius: 12px;
  padding: 16px;
  cursor: pointer;
  box-shadow: 0 4px 14px rgba(201,168,76,0.4);
}
.gp-sticky-btn:disabled { opacity: 0.6; cursor: not-allowed; }
@media (min-width: 641px) {
  .gp-sticky-label { display: block; }
  .gp-sticky-btn { width: auto; white-space: nowrap; padding: 14px 34px; }
}

/* ── Success ── */
.gp-success-icon { font-size: 2.6rem; margin-bottom: 18px; text-align: center; }
.gp-success-title {
  font-family: 'Lora', serif;
  font-size: 1.6rem;
  color: #0f172a;
  text-align: center;
  margin-bottom: 12px;
}
.gp-success-sub {
  font-family: 'Lora', serif;
  font-size: 1rem;
  color: #3c4653;
  line-height: 1.7;
  text-align: center;
  margin-bottom: 28px;
}
.gp-code-box {
  background: #fbf8f1;
  border: 2px dashed #c9a84c;
  border-radius: 12px;
  padding: 22px;
  text-align: center;
  margin-bottom: 24px;
}
.gp-code-label {
  font-family: 'Lora', serif;
  font-size: 0.7rem;
  letter-spacing: 2px;
  color: #6f5f4d;
  text-transform: uppercase;
  margin-bottom: 10px;
}
.gp-code {
  font-family: 'Lora', serif;
  font-size: 1.15rem;
  letter-spacing: 1px;
  color: #7c2d12;
  word-break: break-all;
  line-height: 1.4;
}
.gp-btn-row { display: flex; gap: 12px; flex-wrap: wrap; }
.gp-btn-secondary {
  flex: 1;
  min-width: 160px;
  font-family: 'Lora', serif;
  font-size: 0.95rem;
  font-weight: 600;
  color: #7c2d12;
  background: #fff;
  border: 1px solid #7c2d12;
  border-radius: 10px;
  padding: 14px 20px;
  cursor: pointer;
  text-align: center;
  text-decoration: none;
  transition: all 0.2s;
}
.gp-btn-secondary:hover { background: rgba(201,168,76,0.08); }

/* ── Printable card ── */
.gp-print-wrap {
  width: 100%;
  max-width: 520px;
  margin: 0 auto;
}
.gp-print-card {
  background: #fff;
  border: 1px solid rgba(15,23,42,0.12);
  border-radius: 16px;
  padding: 48px 40px;
  text-align: center;
  position: relative;
}
.gp-print-frame {
  border: 2px solid #c9a84c;
  border-radius: 10px;
  padding: 40px 32px;
}
.gp-print-eyebrow {
  font-family: 'Lora', serif;
  font-size: 0.7rem;
  letter-spacing: 4px;
  color: #6f5512;
  text-transform: uppercase;
  font-weight: 600;
  margin-bottom: 20px;
}
.gp-print-title {
  font-family: 'Lora', serif;
  font-size: 1.7rem;
  color: #0f172a;
  line-height: 1.3;
  margin-bottom: 18px;
}
.gp-print-message {
  font-family: 'Lora', serif;
  font-size: 1.05rem;
  font-style: italic;
  color: #3c4653;
  line-height: 1.7;
  margin-bottom: 24px;
}
.gp-print-divider {
  width: 50px;
  height: 1px;
  background: #d4c4a8;
  margin: 24px auto;
}
.gp-print-instructions {
  font-family: 'Lora', serif;
  font-size: 0.9rem;
  color: #3c4653;
  line-height: 1.6;
  margin-bottom: 18px;
}
.gp-print-url {
  font-family: 'Lora', serif;
  font-size: 0.95rem;
  font-weight: 600;
  color: #7c2d12;
  margin-bottom: 18px;
  word-break: break-all;
}
.gp-print-code-box {
  background: #fbf8f1;
  border: 1px dashed #c9a84c;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 16px;
}
.gp-print-code {
  font-family: 'Lora', serif;
  font-size: 1rem;
  letter-spacing: 1px;
  color: #7c2d12;
  word-break: break-all;
}
.gp-print-expiry {
  font-family: 'Lora', serif;
  font-size: 0.75rem;
  color: #6f5f4d;
}
.gp-print-actions {
  display: flex;
  gap: 12px;
  justify-content: center;
  margin-top: 24px;
}
.gp-print-btn {
  font-family: 'Lora', serif;
  font-size: 0.95rem;
  font-weight: 600;
  color: #fff;
  background: #7c2d12;
  border: none;
  border-radius: 10px;
  padding: 14px 28px;
  cursor: pointer;
  transition: all 0.2s;
}
.gp-print-btn:hover { background: #8f3517; }

/* ── Loading / spinner ── */
.gp-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 60px 24px;
}
.gp-spinner {
  width: 32px;
  height: 32px;
  border: 2px solid rgba(15,23,42,0.1);
  border-top-color: #7c2d12;
  border-radius: 50%;
  animation: gp-spin 0.8s linear infinite;
}
@keyframes gp-spin { to { transform: rotate(360deg); } }

/* ── Footer ── */
.gp-footer {
  width: 100%;
  padding: 24px 32px;
  text-align: center;
  border-top: 1px solid rgba(15,23,42,0.06);
  margin-top: auto;
  background: #fbf8f1;
}
.gp-footer-text {
  font-family: 'Lora', serif;
  font-size: 0.75rem;
  color: #6f5f4d;
  letter-spacing: 1px;
}
.gp-footer-link { color: #3c4653; text-decoration: none; }
.gp-footer-link:hover { color: #0f172a; }
.gp-footer-social { display: flex; gap: 16px; align-items: center; justify-content: center; margin-top: 14px; }
.gp-footer-social a { display: inline-flex; color: #3c4653; transition: color 150ms; }
.gp-footer-social a:hover { color: #0f172a; }
.gp-footer-social svg { width: 20px; height: 20px; display: block; }

/* ── Success / print wrapper (reuses .gp-card inside a padded main) ── */
.gp-main-centred {
  width: 100%;
  max-width: 580px;
  padding: 64px 24px 104px;
  margin: 0 auto;
}

/* ── Print stylesheet ── */
@media print {
  .gp-header, .gp-footer, .gp-print-actions, .gp-no-print { display: none !important; }
  .gp-page { background: #fff; }
  .gp-main-centred { padding: 0; }
  .gp-print-card { border: none; box-shadow: none; padding: 24px; }
  body { background: #fff; }
}
`;

// ═══════════════════════════════════════════════════════════════

function GiftPage({ view = 'buy', code = null }) {
  if (view === 'print') return <PrintCard code={code} />;
  if (view === 'success') return <SuccessView code={code} />;
  return <BuyForm />;
}

// ── Buy form ────────────────────────────────────────────────────
// F-13: `embedded` renders ONLY the checkout card (no Shell/hero/FAQ) for the in-app
// GiftSheet — same state + handleSubmit + Stripe redirect, no fork. The injected STYLES
// are scoped in embedded mode (the global `*` reset is confined to `.gp-embed`, and the
// `body` rule dropped) so landing styles cannot bleed into app chrome (Fable review add).
export function BuyForm({ embedded = false }) {
  const [giverName, setGiverName] = React.useState('');
  const [giverEmail, setGiverEmail] = React.useState('');
  const [recipientName, setRecipientName] = React.useState('');
  const [invalidField, setInvalidField] = React.useState(null);
  const giverNameRef = React.useRef(null);
  const giverEmailRef = React.useRef(null);
  const recipientNameRef = React.useRef(null);
  const [giftMessage, setGiftMessage] = React.useState(DEFAULT_GIFT_NOTE);
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState('');
  const [promoCode, setPromoCode] = React.useState('');
  const [promoValidation, setPromoValidation] = React.useState(null); // {valid, discountCents, finalPriceCents, error} | null
  const [promoLoading, setPromoLoading] = React.useState(false);
  const [cancelled, setCancelled] = React.useState(false);

  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(giverEmail.trim());
  const promoApplied = promoValidation?.valid && typeof promoValidation.finalPriceCents === 'number';
  const displayPrice = promoApplied ? `$${(promoValidation.finalPriceCents / 100).toFixed(2)}` : GIFT_PRICE;
  const isSaleOffer = promoApplied && promoCode === SALE_OFFER.code; // the TALE30 sale (vs a typed gift/other code)

  // F-163-GIFT: auto-apply TALE30 via the shared cap-aware hook (parity with the book flow). Applies only if
  // the server says the discount is live (honest revert to full price if capped/inactive), never clobbers a
  // code the giver typed, and the server re-bakes the real discount at checkout (GIFT-002 preserved).
  const sale = useSaleOffer();
  React.useEffect(() => {
    if (sale.loading || !sale.saleActive) return;
    if (promoCode || promoValidation) return;
    setPromoCode(SALE_OFFER.code);
    setPromoValidation({ valid: true, discountCents: sale.discountCents, finalPriceCents: sale.saleCents, autoApplied: true });
  }, [sale.loading, sale.saleActive, sale.discountCents, sale.saleCents, promoCode, promoValidation]);

  // If the buyer backed out of Stripe checkout, Stripe returns them to /gift?cancelled=1.
  // Reassure them no charge was made and restore what they had typed so they don't start over.
  React.useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get('cancelled') === '1') {
        setCancelled(true);
        const g = sessionStorage.getItem('gift_giverName'); if (g) setGiverName(g);
        const em = sessionStorage.getItem('gift_giverEmail'); if (em) setGiverEmail(em);
        const r = sessionStorage.getItem('gift_recipientName'); if (r) setRecipientName(r);
        const m = sessionStorage.getItem('gift_giftMessage'); if (m) setGiftMessage(m);
      }
    } catch (e) {}
  }, []);

  const applyPromo = async () => {
    if (!promoCode.trim()) return;
    setPromoLoading(true);
    setPromoValidation(null);
    try {
      const resp = await fetch(`${ORDER_API_BASE}/order/validate-promo`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ promoCode: promoCode.trim().toUpperCase(), context: 'gift' }),
      });
      const data = await resp.json().catch(() => ({}));
      setPromoValidation(data);
    } catch {
      setPromoValidation({ valid: false, error: 'Could not check that code. Please try again.' });
    }
    setPromoLoading(false);
  };

  const handleSubmit = async () => {
    // Don't dead-end on a greyed button — point the user to the first missing
    // field: flag it (inline error + red border), scroll it into view, focus it.
    const fail = (field, ref) => {
      setInvalidField(field);
      if (ref.current) {
        ref.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        try { ref.current.focus({ preventScroll: true }); } catch (e) { ref.current.focus(); }
      }
    };
    if (!giverName.trim()) { fail('name', giverNameRef); return; }
    if (!emailValid) { fail('email', giverEmailRef); return; }
    if (!recipientName.trim()) { fail('recipient', recipientNameRef); return; }
    setInvalidField(null);

    setSubmitting(true);
    setError('');
    try {
      const resp = await fetch(`${ORDER_API_BASE}/order/gift-checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          giverName: giverName.trim(),
          giverEmail: giverEmail.trim(),
          recipientName: recipientName.trim(),
          giftMessage: giftMessage.trim(),
          promoCode: promoApplied ? promoCode.trim().toUpperCase() : undefined,
        }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok || !data.checkoutUrl) {
        throw new Error(data.error || 'Could not start checkout. Please try again.');
      }
      // GA4 recommended event — giver is heading to Stripe for a gift purchase.
      trackEvent('begin_checkout', {
        currency: 'USD',
        value: promoApplied ? promoValidation.finalPriceCents / 100 : 89.99,
        coupon: promoApplied ? promoCode.trim().toUpperCase() : undefined,
        item_category: 'gift',
      });
      // Preserve what they typed in case they back out of Stripe (cancel returns to /gift?cancelled=1).
      try {
        sessionStorage.setItem('gift_giverName', giverName.trim());
        sessionStorage.setItem('gift_giverEmail', giverEmail.trim());
        sessionStorage.setItem('gift_recipientName', recipientName.trim());
        sessionStorage.setItem('gift_giftMessage', giftMessage.trim());
        // F-13: mark in-app origin so the success page can offer "Back to your book"
        // (origin can't thread through Stripe from the frontend — success_url is server-side;
        // localStorage survives the round trip on the same domain).
        if (embedded) localStorage.setItem('gift_origin_inapp', String(Date.now()));
      } catch (e) {}
      window.location.href = data.checkoutUrl;
    } catch (err) {
      console.error('Gift checkout error:', err);
      setError(err.message || 'Something went wrong. Please try again.');
      setSubmitting(false);
    }
  };

  const formRef = React.useRef(null);
  const submitRef = React.useRef(null);
  const [ctaInView, setCtaInView] = React.useState(false);
  React.useEffect(() => {
    const el = submitRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => setCtaInView(e.isIntersecting), { threshold: 0.1 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  // Retract the sticky bar while a form field is focused so it never covers the
  // field the user is filling in (e.g. after validation scrolls them to it). The
  // short blur delay avoids a flicker when tabbing between fields.
  const [formFocused, setFormFocused] = React.useState(false);
  React.useEffect(() => {
    const el = formRef.current;
    if (!el) return;
    let t;
    const onIn = () => { clearTimeout(t); setFormFocused(true); };
    const onOut = () => { t = setTimeout(() => setFormFocused(false), 150); };
    el.addEventListener('focusin', onIn);
    el.addEventListener('focusout', onOut);
    return () => { clearTimeout(t); el.removeEventListener('focusin', onIn); el.removeEventListener('focusout', onOut); };
  }, []);

  const scrollToForm = () => {
    if (formRef.current) {
      formRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // F-13: the checkout card — the single source of truth for the form, rendered by BOTH the
  // full public page (inside <Shell>) and the in-app embedded GiftSheet. No fork.
  const checkoutCard = (
          <div className="gp-card gp-card-checkout" ref={formRef}>
            {cancelled && (
              <div style={{ background: '#eef6ee', border: '1px solid #cfe3cf', borderRadius: 10, padding: '12px 14px', marginBottom: 20, fontFamily: "'Lora', serif", fontSize: '0.95rem', color: '#2f5d3a' }}>
                You left checkout. No charge was made. Finish below whenever you are ready.
              </div>
            )}
            <div className="gp-form">
              <div>
                <label className="gp-label" htmlFor="gp-giverName">Your Name</label>
                <input id="gp-giverName" className={`gp-input${invalidField === 'name' ? ' gp-input-error' : ''}`} type="text" value={giverName}
                  ref={giverNameRef} aria-invalid={invalidField === 'name'}
                  onChange={e => { setGiverName(e.target.value); if (invalidField === 'name') setInvalidField(null); }}
                  placeholder="e.g. Sarah" maxLength={100} />
                {invalidField === 'name' && <div className="gp-field-error">Please enter your name.</div>}
              </div>

              <div>
                <label className="gp-label" htmlFor="gp-giverEmail">Your Email</label>
                <input id="gp-giverEmail" className={`gp-input${invalidField === 'email' ? ' gp-input-error' : ''}`} type="email" value={giverEmail}
                  ref={giverEmailRef} aria-invalid={invalidField === 'email'}
                  onChange={e => { setGiverEmail(e.target.value); if (invalidField === 'email') setInvalidField(null); }}
                  placeholder="you@email.com" maxLength={200} />
                {invalidField === 'email' && <div className="gp-field-error">Please enter a valid email address.</div>}
                <div className="gp-hint">
                  We send the gift code and printable card to you, not them.
                  You give the gift, so it stays a surprise.
                </div>
              </div>

              <div>
                <label className="gp-label" htmlFor="gp-recipientName">Recipient's Name</label>
                <input id="gp-recipientName" className={`gp-input${invalidField === 'recipient' ? ' gp-input-error' : ''}`} type="text" value={recipientName}
                  ref={recipientNameRef} aria-invalid={invalidField === 'recipient'}
                  onChange={e => { setRecipientName(e.target.value); if (invalidField === 'recipient') setInvalidField(null); }}
                  placeholder="Who is this gift for?" maxLength={100} />
                {invalidField === 'recipient' && <div className="gp-field-error">Please enter the recipient's name.</div>}
              </div>

              <div>
                <label className="gp-label" htmlFor="gp-giftMessage">A Note for Them (optional)</label>
                <textarea id="gp-giftMessage" className="gp-textarea" value={giftMessage}
                  onChange={e => setGiftMessage(e.target.value.slice(0, 200))}
                  placeholder="Add a personal message. It appears on the gift card."
                  maxLength={200} />
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12 }}>
                  <div className="gp-hint">This note appears on their gift card. Edit it or write your own.</div>
                  <div className="gp-charcount">{giftMessage.length}/200</div>
                </div>
              </div>

              <div>
                <label className="gp-label" htmlFor="gp-promoCode">Promo Code (optional)</label>
                <div className="gp-promo-row">
                  <input id="gp-promoCode" className="gp-input" type="text" value={promoCode}
                    style={{ textTransform: 'uppercase' }}
                    onChange={e => { setPromoCode(e.target.value.trim().toUpperCase()); if (promoValidation) setPromoValidation(null); }}
                    placeholder="Enter code" maxLength={32} />
                  <button type="button" className="gp-promo-btn"
                    disabled={!promoCode.trim() || promoLoading}
                    onClick={applyPromo}>
                    {promoLoading ? '...' : 'Apply'}
                  </button>
                </div>
                {promoApplied && (
                  <p className="gp-promo-ok">
                    {isSaleOffer ? SALE_OFFER.appliedLabel : `Code applied. You save $${(promoValidation.discountCents / 100).toFixed(2)}.`}
                  </p>
                )}
                {promoValidation && !promoValidation.valid && (
                  <p className="gp-promo-err">{promoValidation.error}</p>
                )}
              </div>

              <div className="gp-price-row">
                <span className="gp-price-label">Talebound gift, one hardcover included</span>
                <span className="gp-price-amount">
                  {promoApplied && <span className="gp-price-was">{GIFT_PRICE}</span>}
                  {displayPrice}
                  {isSaleOffer && <span style={{ marginLeft: 8, fontSize: '0.8rem', fontWeight: 700, color: '#2e7d32' }}>{SALE_OFFER.tag}</span>}
                </span>
              </div>

              <div className="gp-includes">
                <div className="gp-includes-item"><span className="gp-includes-check">&#10003;</span> One 8.5" &times; 11" hardcover book, shipped free</div>
                <div className="gp-includes-item"><span className="gp-includes-check">&#10003;</span> Full access to Talebound for the recipient</div>
                <div className="gp-includes-item"><span className="gp-includes-check">&#10003;</span> Refundable if there's ever an issue with your gift</div>
                <div className="gp-includes-item"><span className="gp-includes-check">&#10003;</span> Gift code valid for 12 months</div>
              </div>

              <div className="gp-guarantee">
                <span className="gp-guarantee-icon" aria-hidden="true">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                    <path d="M12 2.5l7.5 2.8v5.7c0 4.7-3.2 8-7.5 10.5-4.3-2.5-7.5-5.8-7.5-10.5V5.3L12 2.5z" fill="rgba(201,168,76,0.12)" stroke="#c9a84c" strokeWidth="1.1" />
                    <path d="M8.4 12.2l2.6 2.5 4.6-5.1" stroke="#6f5512" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </span>
                <div className="gp-guarantee-text">
                  <b>The founder backs every gift.</b> A simple email to <a href="mailto:support@taleboundbook.com">support@taleboundbook.com</a>,
                  and any issue, refund, or question is resolved within 24 hours.
                </div>
              </div>

              {error && <div className="gp-error">{error}</div>}

              <button className="gp-submit" ref={submitRef} onClick={handleSubmit}
                disabled={submitting}>
                {submitting ? 'Taking you to checkout...' : `Continue to checkout • ${displayPrice}`}
              </button>
              <div className="gp-secure">Secure checkout with Stripe. Your gift code arrives by email in minutes.</div>
            </div>
          </div>
  );

  // F-13 embedded (in-app GiftSheet): render ONLY the card, with STYLES scoped so the landing's
  // global `*` reset is confined to `.gp-embed` and the `body` rule is dropped (Fable review) —
  // landing styles cannot bleed into app chrome.
  if (embedded) {
    const embedStyles = STYLES
      .replace('* { margin: 0; padding: 0; box-sizing: border-box; }',
               '.gp-embed, .gp-embed * { margin: 0; padding: 0; box-sizing: border-box; }')
      .replace('body { background: #fff; }', '');
    return (
      <>
        <style>{embedStyles}</style>
        <div className="gp-embed">{checkoutCard}</div>
      </>
    );
  }

  return (
    <Shell>

      {/* ── Band 1: Hero (split: text + photo) ── */}
      <div className="gp-band gp-band-hero">
        <div className="gp-band-inner-wide gp-hero-band">
          <div className="gp-hero-split">
            <div className="gp-hero-text">
              <div className="gp-eyebrow">A Gift Worth Keeping</div>
              <h1 className="gp-hero-title">Give the gift of their life story</h1>
              <p className="gp-hero-sub">
                They don't write a word. They just talk, and it becomes a real hardcover
                book that's theirs to keep forever.
              </p>
              <div className="gp-hero-price">
                {promoApplied && <span className="gp-hero-price-was">{GIFT_PRICE}</span>}
                <span className="gp-hero-price-now">{displayPrice}</span>
                {isSaleOffer && (
                  <span className="gp-hero-price-note">{SALE_OFFER.tag}</span>
                )}
              </div>
              <button className="gp-cta-btn" onClick={scrollToForm} type="button">
                Gift Talebound
              </button>
              <p className="gp-hero-switch">
                This is a gift so <b>they</b> tell their own story. Want to make the book yourself
                with the family? <a href="/celebration">Start a Celebration Book &rarr;</a>
              </p>
            </div>
            <div className="gp-hero-photo">
              <img src="/talebound-books-trio.jpg" alt="Three real Talebound hardcover books in charcoal, burgundy, and floral cover styles" />
              <div className="gp-photo-caption">The cover is theirs to design. Choose a style that fits them, classic to floral, and add any photo.</div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Band 2: How it works ── */}
      <div className="gp-band gp-band-sand">
        <div className="gp-band-inner">
          <div className="gp-eyebrow">How It Works</div>
          <h2 className="gp-section-heading">Three simple steps</h2>
          <p className="gp-section-sub">
            You buy the gift today. They tell their story at their own pace. Their book ships to their door.
          </p>
          <div className="gp-steps">
            <div className="gp-step">
              <div className="gp-step-num">1</div>
              <div className="gp-step-title">Buy the gift today</div>
              <div className="gp-step-text">Your gift code arrives by email in minutes, with a printable card to give them.</div>
            </div>
            <div className="gp-step">
              <div className="gp-step-num">2</div>
              <div className="gp-step-title">They write their story</div>
              <div className="gp-step-text">They create a free account and answer guided questions out loud, at their own pace. Their words become chapters.</div>
            </div>
            <div className="gp-step">
              <div className="gp-step-num">3</div>
              <div className="gp-step-title">Their book arrives</div>
              <div className="gp-step-text">When they finish, the gift code covers their printed hardcover, shipped free to their door.</div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Band 3: Video focal moment ── */}
      <div className="gp-band gp-band-white">
        <div className="gp-band-inner gp-video-band">
          <div className="gp-eyebrow">See It in Action</div>
          <h2 className="gp-section-heading">Watch how it works</h2>
          <p className="gp-section-sub" style={{ margin: '0 auto 0' }}>
            A real conversation. A real book. See exactly what they experience from
            first question to finished page.
          </p>
          <div className="gp-video-focal">
            <VideoEmbed videoId="YBzPWm1EcjU" title="How Talebound works" maxWidth={860} />
          </div>
        </div>
      </div>

      {/* ── Band 4: The magic (talk to book demo) ── */}
      <div className="gp-band gp-band-cream">
        <div className="gp-band-inner gp-magic-section">
          <div className="gp-eyebrow">The Experience</div>
          <h2 className="gp-section-heading">They just talk. Like catching up with an old friend.</h2>
          <p className="gp-section-sub" style={{ margin: '0 auto' }}>
            They sign in and the conversation begins, out loud and in real time.
            A warm interviewer asks the questions and truly listens. All they do is talk.
            And every story they tell comes back written beautifully, in their own voice.
          </p>

          <div className="gp-demo">
            <div className="gp-demo-chat">
              <div className="gp-chat-q">
                <div className="gp-chat-who">Interviewer</div>
                <div className="gp-chat-text">Was there a moment in the week that felt like just yours and your dad's?</div>
              </div>
              <div className="gp-chat-a">
                <div className="gp-chat-who">Them, just talking</div>
                <div className="gp-chat-text">Sundays. He worked nights at the plant so we barely saw him, but Sunday mornings he'd make pancakes. This huge stack.</div>
              </div>
              <div className="gp-chat-q">
                <div className="gp-chat-who">Interviewer</div>
                <div className="gp-chat-text">What do you remember about those mornings?</div>
              </div>
              <div className="gp-chat-a">
                <div className="gp-chat-who">Them, just talking</div>
                <div className="gp-chat-text">Just waiting at the table for him to sit down. He'd let me pour the batter. I still make them the same way, honestly.</div>
              </div>
              <div className="gp-chat-q">
                <div className="gp-chat-who">Interviewer</div>
                <div className="gp-chat-text">Why do you think it stayed with you?</div>
              </div>
              <div className="gp-chat-a">
                <div className="gp-chat-who">Them, just talking</div>
                <div className="gp-chat-text">I don't know. It was the one time the whole week slowed down. It was ours.</div>
              </div>
            </div>
            <div className="gp-demo-arrow">becomes a page of their book</div>
            <div className="gp-demo-book">
              <div className="gp-demo-book-label">A page from their book</div>
              <p className="gp-demo-book-text">
                My father worked nights at the plant, so the week would pass without us seeing
                much of him at all. But every Sunday morning he made pancakes, a great towering
                stack of them, and I would sit at the table waiting for him to take his seat beside
                me. He always let me pour the batter. It was the one hour the week slowed down, the
                one that was ours. I still make them the same way.
              </p>
            </div>
            <p className="gp-demo-foot">
              A few easy questions, a few minutes of just talking, and scattered memories become
              a page they will be proud to hold.
            </p>
          </div>

          <div className="gp-feature-list" style={{ maxWidth: 640, margin: '40px auto 0' }}>
            <div className="gp-feature-item">
              <span className="gp-feature-dot" aria-hidden="true"></span>
              <span>One question at a time, out loud. <b>Never a blank page</b>, never stuck.</span>
            </div>
            <div className="gp-feature-item">
              <span className="gp-feature-dot" aria-hidden="true"></span>
              <span>They add photos and go at <b>their own pace</b>. There's no deadline.</span>
            </div>
            <div className="gp-feature-item">
              <span className="gp-feature-dot" aria-hidden="true"></span>
              <span>When they're ready, it's <b>printed as a real hardcover</b> and shipped to them, free.</span>
            </div>
          </div>

          <div className="gp-qr-callout" style={{ maxWidth: 640, margin: '20px auto 0', textAlign: 'left' }}>
            <div className="gp-qr-art">
              <svg width="38" height="38" viewBox="0 0 44 44" aria-hidden="true">
                <g fill="#1e0e06">
                  <rect x="2" y="2" width="12" height="12" /><rect x="4" y="4" width="8" height="8" fill="#fff" /><rect x="6" y="6" width="4" height="4" />
                  <rect x="30" y="2" width="12" height="12" /><rect x="32" y="4" width="8" height="8" fill="#fff" /><rect x="34" y="6" width="4" height="4" />
                  <rect x="2" y="30" width="12" height="12" /><rect x="4" y="32" width="8" height="8" fill="#fff" /><rect x="6" y="34" width="4" height="4" />
                  <rect x="18" y="4" width="3" height="3" /><rect x="22" y="8" width="3" height="3" /><rect x="18" y="12" width="3" height="3" />
                  <rect x="26" y="18" width="3" height="3" /><rect x="20" y="20" width="3" height="3" /><rect x="34" y="18" width="3" height="3" />
                  <rect x="38" y="22" width="3" height="3" /><rect x="18" y="26" width="3" height="3" /><rect x="30" y="30" width="3" height="3" />
                  <rect x="36" y="30" width="3" height="3" /><rect x="30" y="36" width="3" height="3" /><rect x="36" y="38" width="3" height="3" /><rect x="24" y="34" width="3" height="3" />
                </g>
              </svg>
            </div>
            <div className="gp-qr-text">
              <b>Their voice, printed in the book.</b>
              Add a voice clip or a video and a QR code prints right on the page.
              Scan it anytime to hear them tell it in their own voice.
            </div>
          </div>
        </div>
      </div>

      {/* ── Band 5: Reviews ── */}

      <div className="gp-band gp-band-white">
        <div className="gp-band-inner gp-reviews-band">
          <Reviews title="Loved by families like yours" maxWidth={1000} />
        </div>
      </div>

      {/* ── Band 6: Prompt packs ── */}
      <div className="gp-band gp-band-sand">
        <div className="gp-band-inner gp-packs">
          <div className="gp-eyebrow">No Blank Page, Ever</div>
          <h2 className="gp-section-heading">Pick a vibe. We bring the questions.</h2>
          <p className="gp-section-sub" style={{ margin: '0 auto' }}>
            The hardest part of any life story is knowing where to begin. So they don't. They pick
            a prompt pack, answer questions out loud, and Talebound turns the answers into chapters.
          </p>
          <div className="gp-pack-grid">
            {[
              { name: 'Hot Seat', q: "What's your most useless talent?" },
              { name: 'Trouble I Got Into', q: "What's the most trouble you got into as a kid?" },
              { name: 'How We Fell in Love', q: 'Tell me about your first real date. Be honest.' },
              { name: 'Total Disasters', q: 'A holiday that went hilariously wrong?' },
              { name: 'Takes Me Right Back', q: 'A song that drops you into one exact moment?' },
              { name: 'Firsts', q: 'The first car that was really yours.' },
            ].map(p => (
              <div className="gp-pack-card" key={p.name}>
                <div className="gp-pack-name">{p.name}</div>
                <div className="gp-pack-q">&ldquo;{p.q}&rdquo;</div>
              </div>
            ))}
          </div>
          <p className="gp-pack-foot">Ten packs in all, from warm to cheeky. Pick a mood, answer by talking or typing, and switch anytime.</p>
        </div>
      </div>

      {/* ── Band 7: Send your gift (checkout form) ── */}
      <div className="gp-band gp-band-sand gp-offer-band">
        <div className="gp-band-inner">

          {/* Offer spotlight: the price as a confident moment */}
          <div className="gp-offer-spotlight">
            <div className="gp-offer-headline">Give their life story, beautifully bound</div>
            <div className="gp-offer-price-row">
              {promoApplied && <span className="gp-offer-price-was">{GIFT_PRICE}</span>}
              <span className="gp-offer-price-now">{displayPrice}</span>
              {isSaleOffer && <span style={{ marginLeft: 10, fontSize: '0.85rem', fontWeight: 700, color: '#2e7d32' }}>{SALE_OFFER.tag}</span>}
            </div>
            <div className="gp-offer-code-note">One-time purchase. Free shipping. Gift code valid for 12 months.</div>
          </div>

          <div style={{ textAlign: 'center', marginBottom: 32 }}>
            <div className="gp-eyebrow">Ready to give this</div>
            <h2 className="gp-section-heading">Send your gift</h2>
            <p className="gp-section-sub" style={{ margin: '0 auto' }}>
              Fill this in and check out. Their gift code arrives by email in minutes,
              along with a printable card to give them.
            </p>
          </div>

          {checkoutCard}
        </div>
      </div>

      {/* ── Band 8: FAQ ── */}
      <div className="gp-band gp-band-white">
        <div className="gp-band-inner">
          <div style={{ textAlign: 'center', marginBottom: 40 }}>
            <div className="gp-eyebrow">Before You Buy</div>
            <h2 className="gp-section-heading">Common questions</h2>
          </div>
          <div className="gp-faq">
            <details className="gp-faq-item">
              <summary className="gp-faq-q">Do they need to be good with computers or writing?</summary>
              <div className="gp-faq-a">
                No. They answer one simple question at a time, by typing or just talking out loud.
                Talebound shapes their words into chapters and keeps their voice. There is never a blank page.
              </div>
            </details>
            <details className="gp-faq-item">
              <summary className="gp-faq-q">Is this a subscription? Will there be more charges?</summary>
              <div className="gp-faq-a">
                No. Talebound is a one-time purchase. No monthly fees and no yearly renewals.
                The gift covers their full access and one printed hardcover, and that is it.
              </div>
            </details>
            <details className="gp-faq-item">
              <summary className="gp-faq-q">What does the finished book look like? Can they add photos?</summary>
              <div className="gp-faq-a">
                A real 8.5" by 11" hardcover, printed in full color. Each memory becomes a chapter
                in their own words, and they can add photos to any page. They choose the cover style too.
              </div>
            </details>
            <details className="gp-faq-item">
              <summary className="gp-faq-q">Can they write their book in another language?</summary>
              <div className="gp-faq-a">
                Yes. The app's labels are in English, but they can write and record their whole story
                in another language, such as Spanish, French, Italian, Portuguese, or German. Their
                finished book prints in their own words, in their language.
              </div>
            </details>
            <details className="gp-faq-item">
              <summary className="gp-faq-q">When does the book ship, and who pays for it?</summary>
              <div className="gp-faq-a">
                When they finish writing, the gift code covers their hardcover and free shipping in the USA.
                After they order it, printing and shipping take approximately 7 to 10 days. Nothing ships
                before then, so there is no deadline to hit, and the code stays valid for 12 months.
              </div>
            </details>
            <details className="gp-faq-item">
              <summary className="gp-faq-q">Are their stories private?</summary>
              <div className="gp-faq-a">
                Yes. Their stories stay in their own private account. You will not see them unless
                they choose to share, and nothing is ever made public.
              </div>
            </details>
            <details className="gp-faq-item">
              <summary className="gp-faq-q">What if I change my mind, or they never use it?</summary>
              <div className="gp-faq-a">
                If something isn't right with your gift, just call or email the founder directly using
                the note below. Any issue, refund, or question gets resolved, usually within 24 hours.
              </div>
            </details>
          </div>
        </div>
      </div>

      {/* ── Band 9: Founder note ── */}
      <div className="gp-band gp-band-parchment">
        <div className="gp-band-inner">
          <div className="gp-founder">
            <div className="gp-founder-eyebrow">A Note From the Founder</div>
            <p className="gp-founder-text">
              Hi, I'm Alex. Talebound is a small family company in the USA, and I personally
              stand behind every gift. If you have a question before you buy, or any time after,
              call or email me directly. If I don't pick up, leave a voicemail and I'll call you back.
            </p>
            <div className="gp-founder-contact">
              {FOUNDER_PHONE && (
                <a href={`tel:${FOUNDER_PHONE.replace(/[^+\d]/g, '')}`}>{FOUNDER_PHONE}</a>
              )}
              <a href={`mailto:${FOUNDER_EMAIL}`}>{FOUNDER_EMAIL}</a>
            </div>
            <div className="gp-founder-sig">Alex, Founder of Talebound</div>
          </div>
        </div>
      </div>

      {/* ── Sticky mobile bar ── */}
      <div className={`gp-sticky ${(ctaInView || formFocused) ? 'gp-sticky-hidden' : ''}`}>
        <div className="gp-sticky-inner">
          <div className="gp-sticky-label">Free Shipping &amp; Human Support (USA)</div>
          <button className="gp-sticky-btn" onClick={handleSubmit} disabled={submitting}>
            {submitting ? 'Taking you to checkout...' : `Continue to checkout · ${displayPrice}`}
          </button>
        </div>
      </div>
    </Shell>
  );
}

// ── Success view ────────────────────────────────────────────────
function SuccessView({ code }) {
  const printUrl = code
    ? `${window.location.origin}/gift/print?code=${encodeURIComponent(code)}`
    : '';
  // F-13: if the purchase originated in the app (flag set on embedded submit, survives the
  // Stripe round trip on the same domain), offer a return to the book. 2h freshness window.
  const [fromApp] = React.useState(() => {
    try {
      const t = parseInt(localStorage.getItem('gift_origin_inapp') || '0', 10);
      return !!t && (Date.now() - t) < 2 * 60 * 60 * 1000;
    } catch (e) { return false; }
  });

  // GA4 purchase event — fire once per gift code (guard against refresh double-count).
  // Value comes from `amt` (cents) in the success URL set by the Order Lambda.
  React.useEffect(() => {
    if (!code) return;
    const key = `gift_purchase_tracked_${code}`;
    try { if (sessionStorage.getItem(key)) return; } catch (e) { /* private mode */ }
    const amtCents = parseInt(new URLSearchParams(window.location.search).get('amt'), 10);
    const value = Number.isFinite(amtCents) ? amtCents / 100 : 89.99;
    trackEvent('purchase', {
      transaction_id: code,
      currency: 'USD',
      value,
      items: [{ item_id: 'gift_talebound', item_name: 'Gift someone Talebound', item_category: 'gift', price: value, quantity: 1 }],
    });
    try { sessionStorage.setItem(key, '1'); } catch (e) { /* private mode */ }
  }, [code]);

  return (
    <Shell>
      <div className="gp-main-centred">
        <div className="gp-card">
          <div className="gp-success-icon">🎁</div>
          <div className="gp-success-title">Your gift is on its way</div>
          <div className="gp-success-sub">
            Thank you. We have emailed your gift code and a printable card to your inbox.
            You can also find everything you need right here.
          </div>

          {code ? (
            <>
              <div className="gp-code-box">
                <div className="gp-code-label">Gift Code</div>
                <div className="gp-code">{code}</div>
              </div>
              <div className="gp-btn-row">
                <a className="gp-btn-secondary" href={printUrl}>
                  View printable card
                </a>
                {fromApp ? (
                  <a className="gp-btn-secondary" href="/" onClick={() => { try { localStorage.removeItem('gift_origin_inapp'); } catch (e) {} }}>
                    &larr; Back to your book
                  </a>
                ) : (
                  <a className="gp-btn-secondary" href="https://taleboundbook.com">
                    Back to Talebound
                  </a>
                )}
              </div>
              <div style={{ marginTop: 24, paddingTop: 20, borderTop: '1px solid rgba(15,23,42,0.08)' }}>
                <p style={{ fontFamily: "'Lora', serif", fontSize: '0.9rem', color: '#3c4653', lineHeight: 1.7, textAlign: 'center' }}>
                  Share the code with your recipient. When they are ready to start, they sign up at
                  taleboundbook.com, write their story, and enter this code at checkout to claim their
                  free hardcover. The code is good for 12 months.
                </p>
              </div>
            </>
          ) : (
            <div style={{ textAlign: 'center' }}>
              <p style={{ fontFamily: "'Lora', serif", fontSize: '0.95rem', color: '#3c4653', lineHeight: 1.7 }}>
                Your payment went through. Check your email for the gift code and printable card.
                If it has not arrived in a few minutes, please contact support@taleboundbook.com.
              </p>
            </div>
          )}
        </div>
      </div>
    </Shell>
  );
}

// ── Printable card ──────────────────────────────────────────────
function PrintCard({ code }) {
  const [message, setMessage] = React.useState('');
  const [recipientName, setRecipientName] = React.useState('');
  const [loading, setLoading] = React.useState(true);
  const [valid, setValid] = React.useState(true);

  React.useEffect(() => {
    if (!code) { setValid(false); setLoading(false); return; }
    (async () => {
      try {
        const resp = await fetch(`${ORDER_API_BASE}/order/gift-card?code=${encodeURIComponent(code)}`);
        if (resp.ok) {
          const data = await resp.json().catch(() => ({}));
          setMessage(data.giftMessage || '');
          setRecipientName(data.recipientName || '');
          setValid(data.valid !== false);
        }
      } catch (err) {
        console.error('Gift card fetch error:', err);
      } finally {
        setLoading(false);
      }
    })();
  }, [code]);

  if (loading) {
    return (
      <Shell noindex>
        <div className="gp-loading">
          <div className="gp-spinner" />
          <div style={{ fontFamily: "'Lora', serif", color: '#3c4653', fontSize: '0.9rem', fontStyle: 'italic' }}>
            Preparing your card...
          </div>
        </div>
      </Shell>
    );
  }

  if (!valid || !code) {
    return (
      <Shell noindex>
        <div className="gp-main-centred">
          <div className="gp-card" style={{ textAlign: 'center' }}>
            <div className="gp-success-icon">📭</div>
            <div className="gp-success-title">This card is not available</div>
            <div className="gp-success-sub" style={{ marginBottom: 0 }}>
              The gift code may be invalid or expired. If you believe this is a mistake,
              please contact support@taleboundbook.com.
            </div>
          </div>
        </div>
      </Shell>
    );
  }

  return (
    <Shell noindex>
      <div className="gp-main-centred">
        <div className="gp-print-wrap">
          <p className="gp-no-print" style={{ fontFamily: "'Lora', serif", fontSize: '0.85rem', color: '#535a68', textAlign: 'center', marginBottom: 20, fontStyle: 'italic' }}>
            Print this card or save it as a PDF, then give it to {recipientName || 'your recipient'}.
          </p>
          <div className="gp-print-card">
            <div className="gp-print-frame">
              <div className="gp-print-eyebrow">A Gift for You</div>
              <div className="gp-print-title">
                {recipientName ? `${recipientName}, your story` : 'Your story'} is waiting to be written
              </div>
              {message && <div className="gp-print-message">"{message}"</div>}
              <div className="gp-print-divider" />
              <div className="gp-print-instructions">
                You have been gifted Talebound. Go to the link below, create your free account,
                and write your life story with guided questions every step of the way. Your
                finished hardcover is already paid for, shipping included.
              </div>
              <div className="gp-print-url">taleboundbook.com</div>
              <div className="gp-print-code-box">
                <div className="gp-code-label">Your Gift Code</div>
                <div className="gp-print-code">{code}</div>
              </div>
              <div className="gp-print-expiry">Enter this code at checkout. Valid for 12 months.</div>
            </div>
          </div>
          <div className="gp-print-actions">
            <button className="gp-print-btn" onClick={() => window.print()}>Print this card</button>
          </div>
        </div>
      </div>
    </Shell>
  );
}

// ── Shared shell ────────────────────────────────────────────────
function Shell({ children, noindex }) {
  React.useEffect(() => {
    if (noindex) {
      const meta = document.createElement('meta');
      meta.name = 'robots';
      meta.content = 'noindex';
      document.head.appendChild(meta);
      return () => { document.head.removeChild(meta); };
    }
  }, [noindex]);

  return (
    <>
      <style>{STYLES}</style>
      <div className="gp-page">
        <div className="gp-header">
          <a className="gp-logo" href="https://taleboundbook.com"><span className="gp-logo-name">Talebound</span><span className="gp-logo-tag">Stories bound to last</span></a>
          <a className="gp-nav-link" href="https://taleboundbook.com" target="_blank" rel="noopener noreferrer">Explore Talebound &rarr;</a>
        </div>
        {children}
        <div className="gp-footer">
          <div className="gp-footer-text">
            <a href="https://taleboundbook.com" className="gp-footer-link">Talebound</a>. Stories bound to last.
          </div>
          <div className="gp-footer-social">
            <a href="https://www.instagram.com/taleboundbook/" target="_blank" rel="noopener noreferrer" aria-label="Talebound on Instagram">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
                <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
                <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
              </svg>
            </a>
            <a href="https://www.facebook.com/Taleboundbook" target="_blank" rel="noopener noreferrer" aria-label="Talebound on Facebook">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />
              </svg>
            </a>
          </div>
        </div>
      </div>
    </>
  );
}

export default GiftPage;
